import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { filter, map, startWith, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { ZalamoObject } from '@ui/orders/graphql';
import { UntypedFormControl } from '@angular/forms';
import { TabloFaceToPerson } from '@ui/tablo-generator/types';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { RxjsOperators } from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';

@Component({
    selector: 'lib-single-photo-face-match',
    templateUrl: './single-photo-face-match.component.html',
    styleUrls: ['./single-photo-face-match.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SinglePhotoFaceMatchComponent
  extends AliveState
  implements OnInit
{
  currentFocalPoint = new BehaviorSubject({
    x: 0,
    y: 0,
  });
  pickedPhotoId$ = new BehaviorSubject<{ photoId: string; groupId: string }>(
    null,
  );
  hasFocalPoint$ = this.currentFocalPoint.pipe(map((v) => !!v.x && !!v.y));

  currentPhotoSize = {
    w: 0,
    h: 0,
  };
  @Input() @Required imagesContext: ZalamoObject;
  @Input() @Required contextId;
  scaleFactor = new UntypedFormControl(1);
  rotation = new UntypedFormControl(1);
  memberId$ = new BehaviorSubject<string>(null);
  @Input() set memberId(value: string) {
    this.memberId$.next(value);
  }
  currentFaceMatch = new BehaviorSubject<Record<string, TabloFaceToPerson>>({});
  @Input() set currentFaceToMatch(match: Record<string, TabloFaceToPerson>) {
    this.currentFaceMatch.next(match);
  }

  @Output() faceMatchUpdated = new EventEmitter<{
    x: number;
    y: number;
    scale: number;
    rotation: number;
  }>();
  selfUpdate = false;
  constructor() {
    super();
  }
  pointUpdatedByHand($event: { x: number; y: number }) {
    this.currentFocalPoint.next({
      x: $event.x,
      y: $event.y,
    });
  }

  ngOnInit(): void {
    this.subscribeWhileAlive(
      combineLatest([
        this.currentFaceMatch.pipe(RxjsOperators.filterTruthy()),
        this.memberId$.pipe(RxjsOperators.filterTruthy()),
      ]).pipe(
        tap(([currentConfig, currentUserId]) => {
          if (currentUserId) {
            const currentFace = currentConfig[currentUserId];
            if (currentFace) {
              this.selfUpdate = true;
              this.currentFocalPoint.next({
                x: currentFace.x,
                y: currentFace.y,
              });
              this.scaleFactor.setValue(currentFace.scale);
              this.rotation.setValue(currentFace.rotation);
              this.currentPhotoSize.w = currentFace.width;
              this.currentPhotoSize.h = currentFace.height;
              this.pickedPhotoId$.next({
                photoId: currentFace.photoId,
                groupId: currentFace.groupId,
              });
              this.selfUpdate = false;
            }
          }
        }),
      ),
      combineLatest([
        this.currentFocalPoint.pipe(RxjsOperators.filterTruthy()),
        this.scaleFactor.valueChanges.pipe(
          startWith(this.scaleFactor.value),
          RxjsOperators.filterTruthy(),
        ),
        this.rotation.valueChanges.pipe(
          startWith(this.rotation.value),
          RxjsOperators.filterTruthy(),
        ),
      ]).pipe(
        filter(() => !this.selfUpdate),
        tap(([currentFocalPoint, scale]) => {
          if (this.pickedPhotoId$.value) {
            this.faceMatchUpdated.emit({
              x: currentFocalPoint.x,
              y: currentFocalPoint.y,
              scale: this.scaleFactor.value,
              rotation: this.rotation.value,
            });
          }
        }),
      ),
    );
  }
  async resetRotation() {
    this.rotation.setValue(0);
    const currentFocalPoint = this.currentFocalPoint.value;
    this.faceMatchUpdated.emit({
      x: currentFocalPoint.x,
      y: currentFocalPoint.y,
      scale: this.scaleFactor.value,
      rotation: 0,
    });
  }
}
